import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api, Button, Card, ConfirmDialog, PillButton, apiErrorMessage, formatMoney, brandDisplayName, brandLocalDate, formatIsoDay, useBrand } from '@aralere/shared-ui';
import type {
  Business,
  Remittance,
  RemittanceBalancesResponse,
  RemittanceBrandBalances,
  RemittanceClassBalance,
  RemittanceListResponse,
  RemittanceMethod,
  RemittancePayeeBalance,
  RemittanceStatusFilter,
} from '@aralere/shared-types';

// REMITTANCES (04 #125 WS-B, s193; nav/page label "Remittances" by owner ruling s193, superseding the
// s189 (D) label "Collected for others"): the money-OUT side of a pass-through charge class. A
// pass-through line is collected on behalf of a payee (an agency, a staff member, a tip pool) and sits
// on the books as a LIABILITY until it is paid out; this page is where that payout is recorded and
// where what is still owed is read. Design = the 02g #125 bullet + its s189 AMENDMENT (A)-(G):
//
//   (C) ONE surface, the fourth Billing child; the payee is the header and the class the row, "Owed
//       now $Z" with the derivation one tap away; negative renders red as "Over-paid by". Everything
//       here, the Settings condensed line and the vendor block read ONE derivation (?view=balances);
//       nothing on this page computes a balance.
//   (E) "Record remittance" opens PER PAYEE from the payee header (owner ruling s193, Option 1): one
//       row per pass-through class of that payee, prefilled to its owed figure, editable down to 0;
//       the rows above 0 become the record's allocations and the total is their sum. The method
//       defaults to the payee's last method, else ACH; the reference is REQUIRED (it is the external
//       anchor: there is no document number on a remittance, rule 8).
//   (G) a tips-payable class (241000) carries the payroll hint: the liability clears GROSS; the
//       payroll system handles withholding.
//   Void-not-delete: a voided row stays in the register, marked, with its reason (section 7.15).
//
// Brand scope: balances and the register are PER BRAND (the Settings brand-picker convention; the
// picker collapses when the instance has one brand). `?business=` deep-links a brand (the Settings
// condensed line's "View remittances" link).

const inputClass = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]';
const chip = 'rounded-full px-2 py-0.5 text-[11px] leading-4';

/** Mirror of api/src/lib/remittances.ts REMITTANCE_METHODS (shared-types is type-only). */
const METHOD_OPTIONS: RemittanceMethod[] = ['ach', 'check', 'zelle', 'wire', 'processor-transfer', 'payroll', 'other'];
const METHOD_LABEL: Record<RemittanceMethod, string> = {
  ach: 'ACH', check: 'Check', zelle: 'Zelle', wire: 'Wire', 'processor-transfer': 'Processor transfer', payroll: 'Payroll', other: 'Other',
};
const STATUS_OPTIONS: { value: RemittanceStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'voided', label: 'Voided' },
];
const PAGE_SIZES = [25, 50, 100, 250];
const TIPS_PAYABLE = '241000';
const PAYROLL_HINT = 'Tips payable clears GROSS to payroll; withholding and the employee split happen in the payroll system, not here.';

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;
const isMoneyText = (v: string): boolean => /^\d+(\.\d{1,2})?$/.test(v.trim());
const parseMoney = (v: string): number => (isMoneyText(v) ? round2(Number(v.trim())) : NaN);

const basisLabel = (b: RemittanceClassBalance['remittanceBasis']): string => (b === 'on-issue' ? 'owed when billed' : 'owed as collected');

/** The derivation line (AMENDMENT (C)): on-collection names Billed, Collected and Already paid out;
 *  on-issue skips Collected. A reversal is stated beside Billed only when there is one; tips only
 *  when there are any. */
function derivationOf(c: RemittanceClassBalance): string {
  const billed = `Billed ${formatMoney(c.billed)}${c.reversed > 0 ? ` (after ${formatMoney(c.reversed)} reversed)` : ''}`;
  const collected = `Collected ${formatMoney(c.collected)}${c.tips > 0 ? ` (of which tips ${formatMoney(c.tips)})` : ''}`;
  const paid = `Already paid out ${formatMoney(c.remitted)}`;
  const owed = c.owed < 0 ? `Over-paid by ${formatMoney(-c.owed)}` : `Owed now ${formatMoney(c.owed)}`;
  return c.remittanceBasis === 'on-issue'
    ? `${billed} · ${c.tips > 0 ? `Tips ${formatMoney(c.tips)} · ` : ''}${paid} → ${owed}`
    : `${billed} · ${collected} · ${paid} → ${owed}`;
}

interface DialogRow {
  chargeClassId: string;
  name: string;
  liabilityAccount: string;
  owed: number;
  amount: string;
}

export function AdminRemittances(): JSX.Element {
  const location = useLocation();
  const { brand } = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── brand scope ─────────────────────────────────────────────────────────────
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [businessId, setBusinessId] = useState<string>(searchParams.get('business') ?? '');
  const [bizErr, setBizErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { businesses: list } = await api.admin.listBusinesses();
        if (!alive) return;
        setBusinesses(list);
        setBusinessId((cur) => (cur && list.some((b) => b.id === cur) ? cur : list.find((b) => b.id === brand?.id)?.id ?? list[0]?.id ?? ''));
      } catch (e) {
        if (alive) setBizErr(apiErrorMessage(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [brand?.id]);

  const business = businesses.find((b) => b.id === businessId) ?? null;
  const pickBrand = (id: string): void => {
    setBusinessId(id);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    next.set('business', id);
    setSearchParams(next, { replace: true });
  };

  // ── balances (the ONE derivation) ────────────────────────────────────────────
  const [balances, setBalances] = useState<RemittanceBalancesResponse | null>(null);
  const [balLoading, setBalLoading] = useState(true);
  const [balErr, setBalErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [expanded, setExpanded] = useState<string[]>([]);

  useEffect(() => {
    if (!businessId) return;
    let alive = true;
    setBalLoading(true);
    setBalErr(null);
    void (async () => {
      try {
        const res = await api.admin.remittanceBalances({ businessId });
        if (alive) setBalances(res);
      } catch (e) {
        if (alive) {
          setBalErr(apiErrorMessage(e));
          setBalances(null);
        }
      } finally {
        if (alive) setBalLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [businessId, reloadKey, location.key]);

  const brandBalances: RemittanceBrandBalances | null = balances?.brands.find((b) => b.businessId === businessId) ?? null;
  const toggleExpanded = (id: string): void => setExpanded((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));

  // ── the register ─────────────────────────────────────────────────────────────
  const [payeeFilter, setPayeeFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState<RemittanceStatusFilter>('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [list, setList] = useState<RemittanceListResponse | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listErr, setListErr] = useState<string | null>(null);

  useEffect(() => {
    if (!businessId) return;
    const t = setTimeout(() => {
      void (async () => {
        setListLoading(true);
        setListErr(null);
        try {
          setList(
            await api.admin.listRemittances({
              businessId,
              payeeVendorId: payeeFilter || null,
              chargeClassId: classFilter || null,
              from: from || null,
              to: to || null,
              status,
              page,
              limit,
            }),
          );
        } catch (e) {
          setListErr(apiErrorMessage(e));
          setList(null);
        } finally {
          setListLoading(false);
        }
      })();
    }, 250);
    return () => clearTimeout(t);
  }, [businessId, payeeFilter, classFilter, from, to, status, page, limit, reloadKey, location.key]);

  const onFilter = <T,>(set: (v: T) => void) => (v: T): void => {
    setPage(1);
    set(v);
  };
  const filtersActive = !!(payeeFilter || classFilter || from || to || status !== 'all');
  const clearFilters = (): void => {
    setPage(1);
    setPayeeFilter('');
    setClassFilter('');
    setFrom('');
    setTo('');
    setStatus('all');
  };

  /** Payee + class options for the filters come from the balances view (every payee and class of the
   *  brand), so the filters never depend on what happens to be on the current page. */
  const payeeOptions = useMemo(() => (brandBalances?.payees ?? []).map((p) => [p.payeeVendorId, p.payeeName] as const), [brandBalances]);
  const classOptions = useMemo(
    () => (brandBalances?.payees ?? []).flatMap((p) => p.classes.map((c) => [c.chargeClassId, `${c.chargeClassName} (${p.payeeName})`] as const)),
    [brandBalances],
  );
  const vendorName = (id: string): string => list?.context.vendors[id] ?? brandBalances?.payees.find((p) => p.payeeVendorId === id)?.payeeName ?? id;
  const classLabel = (id: string | null): string => (id ? list?.context.chargeClasses[id] ?? classOptions.find(([cid]) => cid === id)?.[1] ?? id : 'Sales tax');

  // ── record dialog (AMENDMENT (E), Option 1) ──────────────────────────────────
  const [recordFor, setRecordFor] = useState<RemittancePayeeBalance | null>(null);
  const [rows, setRows] = useState<DialogRow[]>([]);
  const [dateRemitted, setDateRemitted] = useState('');
  const [method, setMethod] = useState<RemittanceMethod>('ach');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [recordBusy, setRecordBusy] = useState(false);
  const [recordErr, setRecordErr] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const openRecord = (p: RemittancePayeeBalance): void => {
    setRecordFor(p);
    setRows(
      p.classes.map((c) => ({
        chargeClassId: c.chargeClassId,
        name: c.chargeClassName,
        liabilityAccount: c.liabilityAccount,
        owed: c.owed,
        amount: c.owed > 0 ? c.owed.toFixed(2) : '0.00',
      })),
    );
    setDateRemitted(brandLocalDate(business?.timezone ?? null));
    setMethod(p.lastMethod ?? 'ach');
    setReference('');
    setNotes('');
    setRecordErr(null);
    setResult(null);
  };
  const closeRecord = (): void => {
    setRecordFor(null);
    setRecordErr(null);
  };
  const updateRow = (id: string, amount: string): void => setRows((l) => l.map((r) => (r.chargeClassId === id ? { ...r, amount } : r)));

  const rowProblems = rows.map((r) => {
    if (r.owed <= 0) return null;
    const n = parseMoney(r.amount);
    if (Number.isNaN(n)) return 'Enter an amount with at most 2 decimals.';
    if (n > r.owed + 1e-9) return `At most ${formatMoney(r.owed)} is owed on this class.`;
    return null;
  });
  const total = round2(rows.reduce((s, r) => (r.owed > 0 && isMoneyText(r.amount) ? s + parseMoney(r.amount) : s), 0));
  const recordDisabled = rowProblems.some((p) => p !== null) || total <= 0 || reference.trim() === '' || !/^\d{4}-\d{2}-\d{2}$/.test(dateRemitted);
  const dialogHasTips = rows.some((r) => r.liabilityAccount === TIPS_PAYABLE && r.owed > 0);

  const submitRecord = async (): Promise<void> => {
    if (!recordFor || !businessId) return;
    setRecordBusy(true);
    setRecordErr(null);
    try {
      const res = await api.admin.recordRemittance({
        businessId,
        payeeVendorId: recordFor.payeeVendorId,
        allocations: rows.filter((r) => r.owed > 0 && parseMoney(r.amount) > 0).map((r) => ({ chargeClassId: r.chargeClassId, amount: parseMoney(r.amount) })),
        dateRemitted,
        method,
        reference: reference.trim(),
        notes: notes.trim() || null,
      });
      setResult(`Recorded ${formatMoney(res.remittance.amount)} to ${recordFor.payeeName} (${METHOD_LABEL[res.remittance.method]}, ref ${res.remittance.reference}).`);
      setRecordFor(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setRecordErr(apiErrorMessage(e));
    } finally {
      setRecordBusy(false);
    }
  };

  // ── void dialog ──────────────────────────────────────────────────────────────
  const [voidTarget, setVoidTarget] = useState<Remittance | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidErr, setVoidErr] = useState<string | null>(null);

  const submitVoid = async (): Promise<void> => {
    if (!voidTarget) return;
    setVoidBusy(true);
    setVoidErr(null);
    try {
      await api.admin.voidRemittance(voidTarget.id, voidTarget.businessId, voidReason.trim());
      setResult(`Voided the ${formatMoney(voidTarget.amount)} remittance to ${vendorName(voidTarget.payeeVendorId)} (ref ${voidTarget.reference}). The balance it had relieved is owed again.`);
      setVoidTarget(null);
      setVoidReason('');
      setReloadKey((k) => k + 1);
    } catch (e) {
      setVoidErr(apiErrorMessage(e));
    } finally {
      setVoidBusy(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────────
  const rowsOnPage = list?.remittances ?? [];
  const totalPages = list?.totalPages ?? 0;
  const liveOnPage = round2(rowsOnPage.filter((r) => !r.voided).reduce((s, r) => s + r.amount, 0));
  const noClasses = !balLoading && !balErr && (brandBalances === null || brandBalances.payees.length === 0);

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-display tracking-tight">Remittances</h1>
          <p className="text-sm text-[var(--color-muted)]">
            Money collected on behalf of agencies and staff through pass-through charge classes, what is still owed to each payee, and the remittances that paid it out.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {businesses.length > 1 ? (
            <label className="block">
              <span className="block text-xs text-[var(--color-muted)]">Brand</span>
              <select value={businessId} onChange={(e) => pickBrand(e.target.value)} className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]">
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>{brandDisplayName(b.name)}</option>
                ))}
              </select>
            </label>
          ) : null}
          <Button variant="secondary" onClick={() => setReloadKey((k) => k + 1)} disabled={balLoading || listLoading}>
            {balLoading || listLoading ? 'Loading…' : 'Refresh'}
          </Button>
        </div>
      </div>

      {bizErr ? <Card><div className="p-4 text-sm text-[var(--tint-red-text)]">{bizErr}</div></Card> : null}
      {result ? (
        <Card>
          <div className="flex items-start justify-between gap-3 p-4 text-sm">
            <span>{result}</span>
            <PillButton onClick={() => setResult(null)}>Dismiss</PillButton>
          </div>
        </Card>
      ) : null}

      {/* ── Balances: payee header, class rows (AMENDMENT (C)) ─────────────────────── */}
      <section className="space-y-2">
        <h2 className="text-xs font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">Owed now{business ? ` · ${brandDisplayName(business.name)}` : ''}</h2>
        {balErr ? (
          <Card><div className="p-4 text-sm text-[var(--tint-red-text)]">{balErr}</div></Card>
        ) : balLoading && !balances ? (
          <Card><div className="p-4 text-sm text-[var(--color-muted)]">Loading…</div></Card>
        ) : noClasses ? (
          <Card>
            <div className="space-y-2 p-6 text-sm text-[var(--color-muted)]">
              <p>Nothing is collected on behalf of anyone on this brand yet.</p>
              <p>
                A charge class set to <span className="text-[var(--color-text)]">Books as: Collected on behalf</span> creates the first balance here.{' '}
                <Link className="underline decoration-dotted underline-offset-2 hover:decoration-solid" to="/admin/settings">Open Settings, Charge classes</Link>
              </p>
            </div>
          </Card>
        ) : (
          (brandBalances?.payees ?? []).map((p) => {
            const canRecord = p.classes.some((c) => c.owed > 0);
            return (
              <Card key={p.payeeVendorId}>
                <div className="space-y-3 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{p.payeeName}</span>
                        {!p.payeeActive ? <span className={`${chip} bg-[rgb(var(--edge-rgb)/0.06)] text-[var(--color-muted)]`}>Inactive vendor</span> : null}
                      </div>
                      <div className="text-sm text-[var(--color-muted)]">
                        {p.classes.length === 1 ? 'One class' : `${p.classes.length} classes`}
                        {p.lastMethod ? ` · last paid by ${METHOD_LABEL[p.lastMethod]}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className={`text-right ${p.owed < 0 ? 'text-[var(--tint-red-text)]' : ''}`}>
                        <div className="text-xs text-[var(--color-muted)]">{p.owed < 0 ? 'Over-paid by' : 'Owed now'}</div>
                        <div className="text-lg font-semibold">{formatMoney(Math.abs(p.owed))}</div>
                      </div>
                      <Button onClick={() => openRecord(p)} disabled={!canRecord}>Record remittance</Button>
                    </div>
                  </div>

                  <div className="space-y-1 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-2">
                    {p.classes.map((c) => {
                      const open = expanded.includes(c.chargeClassId);
                      return (
                        <div key={c.chargeClassId} className="text-sm">
                          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                            <div className="flex min-w-0 flex-wrap items-center gap-2">
                              <span className="font-medium">{c.chargeClassName}</span>
                              {c.classStatus !== 'active' ? (
                                <span className={`${chip} ${c.classStatus === 'scheduled' ? 'bg-[var(--tint-warn-bg)] text-[var(--tint-warn-text)]' : 'bg-[rgb(var(--edge-rgb)/0.06)] text-[var(--color-muted)]'}`}>
                                  {c.classStatus === 'scheduled' ? 'Deactivates soon' : 'Inactive'}
                                </span>
                              ) : null}
                              <span className="text-xs text-[var(--color-muted)]">{basisLabel(c.remittanceBasis)} · {c.liabilityAccount}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`font-semibold ${c.owed < 0 ? 'text-[var(--tint-red-text)]' : ''}`}>
                                {c.owed < 0 ? `Over-paid by ${formatMoney(-c.owed)}` : `Owed now ${formatMoney(c.owed)}`}
                              </span>
                              <PillButton onClick={() => toggleExpanded(c.chargeClassId)}>{open ? 'Hide' : 'How'}</PillButton>
                            </div>
                          </div>
                          {open ? (
                            <div className="mt-1 space-y-0.5 text-xs text-[var(--color-muted)]">
                              <div>{derivationOf(c)}</div>
                              {c.liabilityAccount === TIPS_PAYABLE ? <div>{PAYROLL_HINT}</div> : null}
                              {c.owed < 0 ? <div className="text-[var(--tint-red-text)]">A reversal landed after a remittance. Nothing hides this; settle it with the payee and void or re-record as needed.</div> : null}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </section>

      {/* ── The register ─────────────────────────────────────────────────────────── */}
      <section className="space-y-2">
        <h2 className="text-xs font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">Remittances</h2>
        <Card>
          <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted)]">Paid to</span>
              <select className={inputClass} value={payeeFilter} onChange={(e) => onFilter(setPayeeFilter)(e.target.value)}>
                <option value="">Any payee</option>
                {payeeOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted)]">Class</span>
              <select className={inputClass} value={classFilter} onChange={(e) => onFilter(setClassFilter)(e.target.value)}>
                <option value="">Any class</option>
                {classOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted)]">Status</span>
              <select className={inputClass} value={status} onChange={(e) => onFilter(setStatus)(e.target.value as RemittanceStatusFilter)}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted)]">Remitted from</span>
              <input type="date" className={inputClass} value={from} onChange={(e) => onFilter(setFrom)(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted)]">Remitted to</span>
              <input type="date" className={inputClass} value={to} onChange={(e) => onFilter(setTo)(e.target.value)} />
            </label>
          </div>
          {filtersActive ? (
            <div className="flex justify-end border-t border-[rgb(var(--edge-rgb)/0.05)] px-4 py-3">
              <Button variant="ghost" onClick={clearFilters}>Clear filters</Button>
            </div>
          ) : null}
        </Card>

        {listErr ? <Card><div className="p-4 text-sm text-[var(--tint-red-text)]">{listErr}</div></Card> : null}

        <Card>
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 p-4 text-sm">
            <span>
              <span className="font-semibold">{list?.totalCount ?? 0}</span>{' '}
              {(list?.totalCount ?? 0) === 1 ? 'remittance' : 'remittances'}
              {filtersActive ? ' matching' : ''}
            </span>
            <span className="text-[var(--color-muted)]">
              Paid out on this page <span className="font-semibold text-[var(--color-text)]">{formatMoney(liveOnPage)}</span>
            </span>
          </div>
        </Card>

        {listLoading && !list ? (
          <Card><div className="p-4 text-sm text-[var(--color-muted)]">Loading…</div></Card>
        ) : rowsOnPage.length === 0 ? (
          <Card>
            <div className="p-6 text-center text-sm text-[var(--color-muted)]">
              {filtersActive ? 'No remittances match these filters.' : 'No remittances recorded on this brand yet. Record the first one from a payee above.'}
            </div>
          </Card>
        ) : (
          <div className="space-y-2">
            {rowsOnPage.map((r) => (
              <Card key={r.id}>
                <div className={`space-y-2 p-4 ${r.voided ? 'bg-[rgb(var(--edge-rgb)/0.02)]' : ''}`}>
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex flex-wrap items-center gap-2">
                        {r.voided ? <span className={`${chip} bg-[rgb(var(--edge-rgb)/0.06)] text-[var(--color-muted)]`}>Voided</span> : null}
                        <span className={`font-semibold ${r.voided ? 'text-[var(--color-muted)] line-through' : ''}`}>{vendorName(r.payeeVendorId)}</span>
                        <span className="text-sm text-[var(--color-muted)]">{METHOD_LABEL[r.method] ?? r.method} · {formatIsoDay(r.dateRemitted)}</span>
                      </div>
                      <div className="text-sm text-[var(--color-muted)]">
                        Ref <span className="text-[var(--color-text)]">{r.reference}</span>
                        {r.coveredInvoiceIds.length > 0 ? <span> · covers {r.coveredInvoiceIds.length} invoice{r.coveredInvoiceIds.length === 1 ? '' : 's'}</span> : null}
                        {r.notes ? <span> · {r.notes}</span> : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className={`shrink-0 text-right font-semibold ${r.voided ? 'text-[var(--color-muted)] line-through' : ''}`}>{formatMoney(r.amount)}</div>
                      {!r.voided ? (
                        <PillButton tone="red" onClick={() => { setVoidTarget(r); setVoidReason(''); setVoidErr(null); }}>Void remittance</PillButton>
                      ) : null}
                    </div>
                  </div>
                  <div className="space-y-0.5 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-2">
                    {r.allocations.map((a, i) => (
                      <div key={`${a.chargeClassId ?? 'tax'}-${i}`} className="flex items-center justify-between gap-3 text-xs">
                        <span className="min-w-0 truncate">
                          <span className="font-medium text-[var(--color-text)]">{classLabel(a.chargeClassId)}</span>
                          <span className="text-[var(--color-muted)]"> · {a.liabilityAccount}</span>
                        </span>
                        <span className="shrink-0 text-[var(--color-muted)]">{formatMoney(a.amount)}</span>
                      </div>
                    ))}
                  </div>
                  {r.voided ? (
                    <div className="border-t border-[rgb(var(--edge-rgb)/0.05)] pt-2 text-xs text-[var(--color-muted)]">
                      Voided{r.voidedAt ? ` ${formatIsoDay(r.voidedAt.slice(0, 10))}` : ''}{r.voidReason ? ` · ${r.voidReason}` : ''}. The amounts above are what it had relieved before it was reversed.
                    </div>
                  ) : null}
                </div>
              </Card>
            ))}
          </div>
        )}

        {rowsOnPage.length > 0 ? (
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm">
              <label className="flex items-center gap-2">
                <span className="text-xs text-[var(--color-muted)]">Rows per page</span>
                <select className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-2 py-1 text-sm" value={limit} onChange={(e) => onFilter(setLimit)(Number(e.target.value))}>
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              {totalPages > 1 ? (
                <div className="flex items-center gap-3">
                  <Button variant="secondary" disabled={listLoading || (list?.page ?? 1) <= 1} onClick={() => setPage((n) => Math.max(1, n - 1))}>Previous</Button>
                  <span className="text-[var(--color-muted)]">Page {list?.page ?? 1} of {totalPages}</span>
                  <Button variant="secondary" disabled={listLoading || (list?.page ?? 1) >= totalPages} onClick={() => setPage((n) => n + 1)}>Next</Button>
                </div>
              ) : (
                <span className="text-[var(--color-muted)]">{(list?.totalCount ?? 0) === 1 ? 'One remittance fits' : 'All matching remittances fit'} on a single page.</span>
              )}
            </div>
          </Card>
        ) : null}
      </section>

      {/* ── Record remittance (AMENDMENT (E), Option 1: one row per class, prefilled to owed) ── */}
      <ConfirmDialog
        open={recordFor !== null}
        title={recordFor ? `Record remittance to ${recordFor.payeeName}` : 'Record remittance'}
        confirmLabel={recordBusy ? 'Recording…' : `Record ${formatMoney(total)}`}
        busy={recordBusy}
        confirmDisabled={recordDisabled}
        size="lg"
        onConfirm={() => void submitRecord()}
        onCancel={closeRecord}
      >
        <div className="space-y-3 text-sm">
          <p className="text-[var(--color-muted)]">
            One payment, one bank movement. Each class below is prefilled to what is owed on it; lower a row to hold part back, or set it to 0 to leave that class out of this payment.
          </p>
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={r.chargeClassId} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                <div className="min-w-0">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-[var(--color-muted)]">
                    {r.owed > 0 ? `Owed now ${formatMoney(r.owed)}` : r.owed < 0 ? `Over-paid by ${formatMoney(-r.owed)}, nothing to pay` : 'Nothing owed'}
                    {r.liabilityAccount === TIPS_PAYABLE ? ' · tips payable' : ''}
                  </div>
                  {rowProblems[i] ? <div className="text-xs text-[var(--tint-red-text)]">{rowProblems[i]}</div> : null}
                </div>
                <label className="flex items-center gap-2">
                  <span className="text-xs text-[var(--color-muted)]">$</span>
                  <input
                    className={`${inputClass} w-32 text-right`}
                    inputMode="decimal"
                    value={r.amount}
                    disabled={r.owed <= 0 || recordBusy}
                    onChange={(e) => updateRow(r.chargeClassId, e.target.value)}
                  />
                </label>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-[rgb(var(--edge-rgb)/0.05)] pt-2 font-semibold">
            <span>Total</span>
            <span>{formatMoney(total)}</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted)]">Date remitted</span>
              <input type="date" className={inputClass} value={dateRemitted} disabled={recordBusy} onChange={(e) => setDateRemitted(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted)]">Method</span>
              <select className={inputClass} value={method} disabled={recordBusy} onChange={(e) => setMethod(e.target.value as RemittanceMethod)}>
                {METHOD_OPTIONS.map((m) => (
                  <option key={m} value={m}>{METHOD_LABEL[m]}</option>
                ))}
              </select>
            </label>
          </div>
          {dialogHasTips && method === 'payroll' ? <p className="text-xs text-[var(--color-muted)]">{PAYROLL_HINT}</p> : null}
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[var(--color-muted)]">Reference (required)</span>
            <input className={inputClass} value={reference} maxLength={200} disabled={recordBusy} placeholder="Check number, ACH trace, payroll run, agency confirmation" onChange={(e) => setReference(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[var(--color-muted)]">Notes</span>
            <textarea className={`${inputClass} min-h-[56px]`} value={notes} maxLength={2000} disabled={recordBusy} onChange={(e) => setNotes(e.target.value)} />
          </label>
          {recordErr ? <p className="text-[var(--tint-red-text)]">{recordErr}</p> : null}
        </div>
      </ConfirmDialog>

      {/* ── Void remittance (void-not-delete, reason required) ─────────────────────── */}
      <ConfirmDialog
        open={voidTarget !== null}
        title="Void remittance"
        confirmLabel={voidBusy ? 'Voiding…' : 'Void remittance'}
        confirmVariant="danger"
        busy={voidBusy}
        confirmDisabled={voidReason.trim() === ''}
        onConfirm={() => void submitVoid()}
        onCancel={() => { setVoidTarget(null); setVoidErr(null); }}
      >
        {voidTarget ? (
          <div className="space-y-3 text-sm">
            <p>
              This reverses the {formatMoney(voidTarget.amount)} remittance to {vendorName(voidTarget.payeeVendorId)} (ref {voidTarget.reference}). The classes it relieved become owed again; the row stays in the register, marked void.
            </p>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted)]">Reason (required)</span>
              <textarea className={`${inputClass} min-h-[56px]`} value={voidReason} maxLength={500} disabled={voidBusy} onChange={(e) => setVoidReason(e.target.value)} />
            </label>
            {voidErr ? <p className="text-[var(--tint-red-text)]">{voidErr}</p> : null}
          </div>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}
